import { useContext, useEffect, useState } from "react";
import { useParams } from "react-router";

import { FaFilter, FaDownload } from "react-icons/fa";
import { Button } from "react-bootstrap";
import { getManagerUrl } from "@/config";
import ControlledTable, { IColumn } from "../../../manager/components/controlled-table/controlled-table";
import { ISortedBy, ITableRowData } from "../../../manager/components/data-table/data-table";
import Page from "../../../manager/components/page/page";
import SearchInput from "../../../manager/components/search-input/search-input";
import { noop } from "../../../manager/utils";
import Spinner from "../../../shared/components/spinner/spinner";
import TextInput from "../../../shared/components/text-input/text-input";
import AuthContext from "../../../shared/context/auth-context";
import { IEnumerationOption } from "../../../shared/definitions/datatypes";
import useHttp from "../../../shared/hooks/use-http";
import { downloadFile, getAllData, postSingle } from "../../../shared/utils/api";
import "./datamodel-report-details.scss";
import { IObject, validateLoadingStatusNew } from "../../../shared/utils/utils";
import { IQueryFilter } from "../datamodel-report-new/datamodel-report-new";
import { dateOptions, operatorByType, stateOptions } from "../datamodel-report-new/datamodel-report-utils";
import { useHttpNonTable } from "../../../shared/hooks/use-httpNonTable";


interface IDatamodelReportDetailFilterData {
  report_id: string;
  query_filters:
  IQueryFilter[] | undefined;
}

interface IDatamodelReportDetailData {
  report_id: string;
  name: string;
  created_at: string;
  datamodel_type: string;
  datamodel_id: string;
  query_filters: IQueryFilter[] | undefined;
  columns: IColumn[];
  rows: ITableRowData[];
} 

const DatamodelReportDetails = () => {
  const authCtx = useContext(AuthContext);
  const params = useParams();
  const hasManager = getManagerUrl() !== "";

  const [showFilters, setShowFilters] = useState(false);
  const [currentReportData, setCurrentReportData] = useState<IDatamodelReportDetailData>({
    report_id: "",
    name: "",
    created_at: "",
    datamodel_type: "",
    datamodel_id: "",
    query_filters: undefined,
    columns: [],
    rows: [],
  });
  const [currentFilters, setCurrentFilters] = useState<IDatamodelReportDetailFilterData["query_filters"]>([]);
  const [filterKey, setFilterKey] = useState(0);
  const [initialFilterData, setInitialFilterData] = useState<IDatamodelReportDetailFilterData>({
    report_id: "",
    query_filters: undefined,
  });
  const [sortedByCol, setSortedByCol] = useState<ISortedBy>({column: "default", direction: "asc"});
  const [hasAppliedFilters, setHasAppliedFilters] = useState(false);
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [allResults, setAllResults] = useState<any[]>([]); // eslint-disable-line

  const { sendRequest: getReport, status: getReportStatus, data: getReportData, error: errorGetReportData } = useHttp(getAllData, true, authCtx.token, "datamodelReport", "platform");
  const { sendRequest: postFilterData, status: postFilterDataStatus, data: postFilterDataResult, error: postFilterDataError } = useHttp(postSingle, false, authCtx.token, "datamodelReportFiltering", "platform");
  const { sendRequest: exportReport, status: exportReportStatus, data: exportReportResult, error: exportReportError } = useHttpNonTable(downloadFile, false, authCtx.token, "document", "platform");
  const {sendRequest: getProcessTypes, data: getProcessTypesData} = useHttp(getAllData, false, authCtx.token, "default", "manager");  
  const {sendRequest: getDomainTypes, data: getDomainTypesData} = useHttp(getAllData, false, authCtx.token, "default", "manager");  
  const {sendRequest: getUsers, data: getUsersData} = useHttp(getAllData, false, authCtx.token, "default", "platform");  

  useEffect(() => {
    if (params.id) {
      setOffset(0);
      setAllResults([]);
      setHasMore(true);
      setIsLoading(true);

      getReport(`report/${params.id}?order_by=${sortedByCol.column} ${sortedByCol.direction}&limit=50&offset=0`);
      setHasAppliedFilters(false);
    }
  }, [params.id]);

  useEffect(() => { 
    if (getReportStatus === "completed" && !errorGetReportData && getReportData) {
      setIsLoading(false);
      setIsLoadingMore(false);
      if (offset === 0) {
        setAllResults(getReportData.rowData);
      } else {
        setAllResults((prev) => [...prev, ...getReportData.rowData]);
      }

      if (getReportData.rowData.length < 50) {
        setHasMore(false);
      }
    }
  }, [getReportStatus, getReportData, offset]);

  useEffect(() => {
    if (getReportStatus === "completed" && !getReportData?.datamodel_id && hasManager){
      getProcessTypes("all_process_types");
      getDomainTypes("domain_types");
      getUsers("users");
    }
  },[getReportStatus]);

  useEffect(() => {
    if (params.id && sortedByCol.column !== "default") {
      setOffset(0);
      setAllResults([]);
      setHasMore(true);
      setIsLoading(true);
      
      if (hasAppliedFilters) {
        postFilterData(`report_filtering/${params.id}?order_by=${sortedByCol.column} ${sortedByCol.direction}`, {
          report_id: params.id || "",
          query_filters: currentFilters,
        }, undefined, "none");
      } else {
        getReport(`report/${params.id}?order_by=${sortedByCol.column} ${sortedByCol.direction}&limit=50&offset=0`);
      }
    }
  }, [sortedByCol]);

  useEffect(() => {
    if (getReportStatus === "completed" && !errorGetReportData && getReportData) {
      const filterData = {
        report_id: getReportData?.report_id || "",
        query_filters: getReportData?.query_filters || undefined,
      };
      setInitialFilterData(filterData);
      setCurrentFilters(filterData.query_filters ? [...filterData.query_filters] : []);
      setCurrentReportData({
        report_id: getReportData?.report_id || "",
        name: getReportData?.name || "",
        created_at: getReportData?.created_at || "",
        datamodel_type: getReportData?.datamodel_type || "",
        datamodel_id: getReportData?.datamodel_id || "",
        query_filters: getReportData?.query_filters || undefined,
        columns: getReportData?.columns || [],
        rows: getReportData?.rowData || [],
      });
    }
  }, [getReportData, getReportStatus]);

  const updateQueryFilter = (index: number, field: string, value: string) => {
    setCurrentFilters(prev => {
      if (!prev) return prev;
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
  };

  const applyFilters = () => {
    const sortParam = sortedByCol.column !== "default" ? `?order_by=${sortedByCol.column} ${sortedByCol.direction}` : "";
    postFilterData(`report_filtering/${params.id}${sortParam}`, {
      report_id: params.id || "",
      query_filters: currentFilters,
    }, undefined, "none");
    setShowFilters(false);
    setHasAppliedFilters(true);
  };

  useEffect(() => {
    if (postFilterDataStatus === "completed" && !postFilterDataError && postFilterDataResult) {
      setIsLoading(false);
      setCurrentReportData({
        report_id: postFilterDataResult?.report_id || "",
        name: postFilterDataResult?.name || "",
        created_at: postFilterDataResult?.created_at || "",
        datamodel_type: postFilterDataResult?.datamodel_type || "",
        datamodel_id: postFilterDataResult?.datamodel_id || "",
        query_filters: postFilterDataResult?.query_filters || undefined,
        columns: postFilterDataResult?.columns || [],
        rows: postFilterDataResult?.rowData || [],
      });
      // Update when filters are applied
      setAllResults(postFilterDataResult?.rowData || []);
      setHasMore(false);
      const filterData = {
        report_id: postFilterDataResult?.report_id || "",
        query_filters: postFilterDataResult?.query_filters || undefined,
      };
      setInitialFilterData(filterData);
    }
  }, [postFilterDataResult, postFilterDataStatus]);

  useEffect(() => {
    if (exportReportStatus === "completed" && !exportReportError && exportReportResult) {
      const url = window.URL.createObjectURL(exportReportResult.file);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", currentReportData?.name || "");
      document.body.appendChild(link);
      link.click();
      link.remove();
    }
  }, [exportReportStatus]);

  const handleExportCsv = () => {
    const filtersStr = encodeURIComponent(JSON.stringify(currentFilters));
    exportReport(`export_report/${params.id}?currentFilters=${filtersStr}`);
  };

  const resetFilters = () => {
    const resetFilters = getReportData?.query_filters ? [...getReportData.query_filters] : [];
    setCurrentFilters(resetFilters);
    setFilterKey(prev => prev + 1);
    setHasAppliedFilters(false); 
    setOffset(0);
    setHasMore(true);
    getReport(`report/${params.id}?order_by=${sortedByCol.column} ${sortedByCol.direction}&limit=50&offset=0`);
  };

  const handleScroll = () => {
    if (!isLoadingMore && hasMore && !hasAppliedFilters) {
      const newOffset = offset + 50;
      setOffset(newOffset);
      setIsLoadingMore(true);
      getReport(`report/${params.id}?order_by=${sortedByCol.column}%20${sortedByCol.direction}&limit=50&offset=${newOffset}`);
    }
  };
  const toggleFilters = () => {
    setShowFilters(!showFilters);
    if (!showFilters) {
      setCurrentFilters(initialFilterData.query_filters ? [...initialFilterData.query_filters] : []);
      setFilterKey(prev => prev + 1);
    }
  };

  const renderFilterValue = (filter: IQueryFilter, index: number) => {
    let filterOptions = [];
    if (filter.field === "process_type_id") {
      filterOptions = getProcessTypesData?.map((processType: IObject) => ({ 
        id: processType.id, 
        name: processType.name 
      })) || [];
    }
    if (filter.field === "domain_type_id") {
      filterOptions = getDomainTypesData?.map((domainType: IObject) => ({ 
        id: domainType.id, 
        name: domainType.name 
      })) || [];
    }
    if (filter.field === "last_changed_by_id" || filter.field === "assigned_to") {
      filterOptions = getUsersData?.map((user: IObject) => ({ 
        id: user.id, 
        name: user.name 
      })) || [];
    }
    if (filter.field === "state") {
      filterOptions = stateOptions;
    }
    if (filter.field_type === "datetime") {
      filterOptions = dateOptions;
    }
    if (filter.field_type === "float" || filter.field_type === "integer") {
      return <TextInput
        type="number"
        label="Filter value"
        id={`filterValue-${index}`}
        required={true}
        inputKey={`filterValue-${index}`}
        handleOnChange={(selectedText: string) => {
          updateQueryFilter(index, "value", selectedText);
        }}
        placeholder="Enter filter value"
        value={filter.value}
        autofocus={false}
        defaultValue={filter.value}
      />;
    }
    if (filterOptions.length === 0 && filter.field_type === "text") {
      return <TextInput
        type="text"
        label="Filter value"
        id={`filterValue-${index}`}
        required={true}
        inputKey={`filterValue-${index}`}
        handleOnChange={(selectedText: string) => {
          updateQueryFilter(index, "value", selectedText);
        }}
        placeholder="Enter filter value"
        value={filter.value}
        autofocus={false}
        defaultValue={filter.value}
      />;
    }

    return <SearchInput
      label="Filter Value"
      handleOnChange={(selectedOption: IEnumerationOption | undefined) => {
        updateQueryFilter(index, "value", selectedOption?.id || "");
      }}
      data={filterOptions}
      selectedOption={filter.value}
      inputKey={`filter-domain-type-${index}`}
      required={true}
    />;
  };

  return (
    <div className="datamodelReportDetails">
      {validateLoadingStatusNew([getReportStatus, postFilterDataStatus, exportReportStatus]) && <Spinner />}
      <Page title={{
        text: getReportData?.name,
      }}>
        <div className="datamodelReportDetails-controls">
          <Button
            onClick={toggleFilters}
          >
            <FaFilter />
          </Button>
          <Button
            onClick={handleExportCsv}
            disabled={currentReportData.rows.length === 0}
          >
            <FaDownload /> Export
          </Button>
        </div>

        {showFilters && currentFilters?.length !== 0 && (
          <div className="datamodelReportDetails-filters">
            <div className="datamodelReportDetails-filtersHeader">
              <h4>Filter Options</h4>
            </div>

            <div>
              {currentFilters?.map((filter, index) => (
                <div key={`${filterKey}-${index}`} className="datamodelReportDetails-queryFilter">
                  <span className="datamodelReportDetails-queryFilterHeaderTitle">
                      Filter {index + 1}: {filter.field}
                  </span>
                  <div className="datamodelReportDetails-queryFilterRow">
                    <SearchInput
                      label={"Filter operator"}
                      handleOnChange={(selectedOption: IEnumerationOption | undefined) => {
                        updateQueryFilter(index, "op", selectedOption?.id || "");
                      }}
                      data={ operatorByType[filter.field_type] }
                      selectedOption={filter.op || (filter.field_type === "datetime" ? "=" : "")}
                      inputKey={`filter-operator-${filterKey}-${index}`}
                      required={true}
                    />
                    {renderFilterValue(filter, index)}
                  </div>
                </div>
              ))}
            </div>
            <div className="datamodelReportDetails-filtersActions">
              <Button
                onClick={resetFilters}
              >
                    Reset
              </Button>
              <Button
                onClick={applyFilters}
              >
                    Apply Filters
              </Button>
            </div>
          </div>
        )}

        {showFilters && (!currentFilters || currentFilters.length === 0) && (
          <div className="datamodelReportDetails-noFilters">
            No filters available for this report.
          </div>
        )}

        <div className="datamodelReportDetails-table">
        <div className="documentSearch-container">
          {(isLoadingMore || isLoading) && <Spinner />}
        </div>
          <ControlledTable
            columns={currentReportData?.columns}
            data={allResults || []}
            selectionMode="off"
            onSorted={setSortedByCol}
            onRowClick={noop}
            readOnly={true}
            onScroll={handleScroll}
            decimalFormatting={true}
          />
        </div>
      </Page>
    </div>
  );
};

export default DatamodelReportDetails;